import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { AppHeaderProvider } from '../contexts/AppHeaderContext';
import AppHeader from './AppHeader';
import SettingsModal from './SettingsModal';
import SyncModal from './SyncModal';
import StravaConnectDocModal from './StravaConnectDocModal';
import ServicesModal from './ServicesModal';
import ProfileModal from './ProfileModal';
import StravaSyncListener from './StravaSyncListener';
import ExpirationWarningBanner from './ExpirationWarningBanner';

/**
 * Shared layout for authenticated pages. Renders the application header
 * (sticky, visible across all pages) and the page content via Outlet.
 * The header must stay visible on Dashboard, Activity Detail, and any future pages.
 */
const AppLayout = () => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [stravaDocModalOpen, setStravaDocModalOpen] = useState(false);
  const [servicesModalOpen, setServicesModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  useEffect(() => {
    const handleOpenSettings = () => setSettingsOpen(true);
    window.addEventListener('openSettings', handleOpenSettings);
    return () => window.removeEventListener('openSettings', handleOpenSettings);
  }, []);

  useEffect(() => {
    const handleOpenSyncModal = () => setSyncOpen(true);
    window.addEventListener('openSyncModal', handleOpenSyncModal);
    return () => window.removeEventListener('openSyncModal', handleOpenSyncModal);
  }, []);

  useEffect(() => {
    const handleOpenStravaDocModal = () => setStravaDocModalOpen(true);
    window.addEventListener('openStravaConnectDocModal', handleOpenStravaDocModal);
    return () => window.removeEventListener('openStravaConnectDocModal', handleOpenStravaDocModal);
  }, []);

  useEffect(() => {
    const handleOpenServices = () => setServicesModalOpen(true);
    window.addEventListener('openServicesModal', handleOpenServices);
    return () => window.removeEventListener('openServicesModal', handleOpenServices);
  }, []);

  useEffect(() => {
    const handleOpenProfile = () => setProfileModalOpen(true);
    window.addEventListener('openProfileModal', handleOpenProfile);
    return () => window.removeEventListener('openProfileModal', handleOpenProfile);
  }, []);

  return (
    <AppHeaderProvider>
      <StravaSyncListener />
      <div className='citius-screenshot-app-root min-h-screen bg-background'>
        <AppHeader />
        {/* No vertical padding when the banner is empty — avoids a transparent strip under the header. */}
        <div className='px-4'>
          <ExpirationWarningBanner />
        </div>
        <main>
          <Outlet />
        </main>
      </div>
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <SyncModal isOpen={syncOpen} onClose={() => setSyncOpen(false)} />
      <StravaConnectDocModal
        isOpen={stravaDocModalOpen}
        onClose={() => setStravaDocModalOpen(false)}
      />
      <ServicesModal isOpen={servicesModalOpen} onClose={() => setServicesModalOpen(false)} />
      <ProfileModal isOpen={profileModalOpen} onClose={() => setProfileModalOpen(false)} />
    </AppHeaderProvider>
  );
};

export default AppLayout;
